import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  Users, 
  DollarSign, 
  Link as LinkIcon, 
  Copy, 
  Check,
  Loader2,
  TrendingUp,
  Clock,
  Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import * as api from '@/lib/api';

const MetricCard = ({ title, value, icon: Icon, subtitle }) => (
  <Card className="bg-card border-border">
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-muted-foreground">{title}</p>
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
      <p className="text-3xl font-heading font-bold">{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
);

export default function Affiliates() {
  const { user, refreshUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [copied, setCopied] = useState(false);
  const [payoutEmail, setPayoutEmail] = useState('');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.getAffiliateDashboard();
      setDashboard(res.data);
      setPayoutEmail(res.data.payout_email || user?.email || '');
    } catch (error) {
      logger.error('Failed to fetch affiliate dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    if (user?.is_affiliate) {
      fetchDashboard();
    } else {
      setLoading(false);
    }
  }, [user?.is_affiliate, fetchDashboard]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      await api.joinAffiliateProgram();
      toast.success('Welcome to the affiliate program!');
      await refreshUser();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    } finally {
      setJoining(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(dashboard?.referral_link || '');
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(dashboard?.affiliate_code || '');
    toast.success('Code copied to clipboard!');
  };

  const handleUpdatePayout = async () => {
    try {
      await api.updatePayoutSettings(payoutEmail, 'paypal');
      toast.success('Payout settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not an affiliate yet
  if (!user?.is_affiliate) {
    return (
      <div className="max-w-3xl mx-auto py-12" data-testid="affiliates-page">
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-heading font-bold mb-4">
              Earn 30% Recurring Commissions
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Join our affiliate program and earn money every month for every 
              customer you refer. Commission is paid as long as they remain subscribed.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10 max-w-2xl mx-auto">
              <div className="text-center">
                <p className="text-4xl font-bold text-primary mb-2">30%</p>
                <p className="text-sm text-muted-foreground">Commission Rate</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-primary mb-2">∞</p>
                <p className="text-sm text-muted-foreground">Recurring Forever</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-bold text-primary mb-2">$50</p>
                <p className="text-sm text-muted-foreground">Min Payout</p>
              </div>
            </div>

            <Button 
              size="lg" 
              onClick={handleJoin} 
              disabled={joining}
              className="px-8 glow-primary"
              data-testid="join-affiliate-btn"
            >
              {joining ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Users className="w-5 h-5 mr-2" />
                  Join Affiliate Program
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Affiliate Dashboard
  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="affiliates-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold">Affiliate Dashboard</h1>
        <p className="text-muted-foreground">Track your referrals and earnings</p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Referrals"
          value={dashboard?.total_referrals || 0}
          icon={Users}
        />
        <MetricCard
          title="Active Referrals"
          value={dashboard?.active_referrals || 0}
          icon={TrendingUp}
          subtitle="Paying customers"
        />
        <MetricCard
          title="Total Earnings"
          value={`$${(dashboard?.total_earnings || 0).toFixed(2)}`}
          icon={DollarSign}
        />
        <MetricCard
          title="Pending Payout"
          value={`$${(dashboard?.pending_payout || 0).toFixed(2)}`}
          icon={Wallet}
          subtitle="Min $50 to withdraw"
        />
      </div>

      {/* Referral Link */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Your Referral Link</CardTitle>
          <CardDescription>Share this link to earn commissions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Input
              value={dashboard?.referral_link || ''}
              readOnly
              className="bg-muted/50"
              data-testid="referral-link-input"
            />
            <Button onClick={copyLink} variant="outline" data-testid="copy-link-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Your Code</p>
              <Badge 
                className="text-lg px-4 py-2 cursor-pointer hover:bg-primary/20" 
                variant="outline"
                onClick={copyCode}
              >
                {dashboard?.affiliate_code}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Commission Rate</p>
              <Badge className="text-lg px-4 py-2 bg-green-500/20 text-green-500 border-green-500/30">
                {dashboard?.commission_rate}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payout Settings */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Payout Settings</CardTitle>
          <CardDescription>Configure how you receive your commissions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md">
            <Label>PayPal Email</Label>
            <div className="flex gap-3 mt-2">
              <Input
                type="email"
                value={payoutEmail}
                onChange={(e) => setPayoutEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="payout-email-input"
              />
              <Button onClick={handleUpdatePayout} data-testid="update-payout-btn">
                Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Payouts are processed monthly when balance exceeds $50
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Referrals */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Recent Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_referrals?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_referrals.map((referral, index) => (
                  <div key={`referral-${referral.status}-${index}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">New referral</span>
                    </div>
                    <Badge variant="outline" className={
                      referral.status === 'active' 
                        ? 'bg-green-500/20 text-green-500 border-green-500/30' 
                        : ''
                    }>
                      {referral.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No referrals yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Commissions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Recent Commissions</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_commissions?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_commissions.map((commission, index) => (
                  <div key={`commission-${commission.status}-${index}`} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium">${commission.amount.toFixed(2)}</span>
                    </div>
                    <Badge variant="outline" className={
                      commission.status === 'paid' 
                        ? 'bg-green-500/20 text-green-500 border-green-500/30' 
                        : ''
                    }>
                      {commission.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center">
                <DollarSign className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No commissions yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
